import { Component, Inject, OnInit } from '@angular/core';
import {
  DwComponent,
  DwComponentType,
  DwContextService,
  DwModalButtonStandardConfigs,
  DwModalConfig,
  DwModalService,
  DwModalSize,
  DwOrmDataService,
  DwOrmDataServiceToken
} from '@devwareapps/devware-cap';
import { forkJoin, Observable, of, throwError } from 'rxjs';
import { map, mergeMap, take } from 'rxjs/operators';
import {
  ReportDashboardCreateModalComponent,
  ReportDashboardCreateModalResult
} from '../report-dashboard-create-modal/report-dashboard-create-modal.component';
import {
  ReportDashboardGroupModalComponent,
  ReportDashboardGroupModalResult,
  ReportDashboardPendingClone
} from '../report-dashboard-group-modal/report-dashboard-group-modal.component';
import {
  ReportDashboardConfigRecord,
  ReportDashboardGroupConfigRecord
} from '../../models/report-dashboard-config-record.model';
import { ReportDashboardDefinition } from '../../models/report-dashboard-definition.model';
import {
  ReportDashboardGroupDefinition,
  ReportDashboardGroupTabDefinition
} from '../../models/report-dashboard-group-definition.model';
import { ReportDashboardConfigService } from '../../services/report-dashboard-config.service';
import { AdminManagerService } from '../../../../shared/services/admin-manager.service';

type ReportConfigurationType = 'group' | 'dashboard' | 'widget';

@DwComponent({
  key: 'report-dashboard-designer',
  name: 'Report Dashboard Designer',
  componentType: DwComponentType.pageComponent,
  isGlobal: true
})
@Component({
  selector: 'app-report-dashboard-designer',
  templateUrl: './report-dashboard-designer.component.html',
  styleUrls: ['./report-dashboard-designer.component.scss']
})
export class ReportDashboardDesignerComponent implements OnInit {
  configurationType: ReportConfigurationType = 'dashboard';
  records: any[] = [];
  selectedRecord: any;
  configText = '';
  workingDefinition: any;
  previewDefinition: ReportDashboardDefinition;
  previewVisible = false;
  editorMode: 'design' | 'json' = 'design';
  reusableWidgetRecords: any[] = [];
  dashboardGroupRecords: ReportDashboardGroupConfigRecord[] = [];
  selectedDashboardGroupRecord: ReportDashboardGroupConfigRecord;
  selectedDashboardGroupDefinition: ReportDashboardGroupDefinition;
  loading = false;
  saving = false;
  savingGroup = false;
  creatingDashboard = false;
  statusMessage: string;
  errorMessage: string;
  expressionContext: any;
  private originalRecordSnapshot: any;

  readonly configurationTypes: Array<{ key: ReportConfigurationType; display: string }> = [
    { key: 'group', display: 'Groups' },
    { key: 'dashboard', display: 'Dashboards' },
    { key: 'widget', display: 'Reusable Widgets' }
  ];

  constructor(
    private configService: ReportDashboardConfigService,
    @Inject(DwOrmDataServiceToken) private ormDataService: DwOrmDataService,
    private modalService: DwModalService,
    private contextService: DwContextService,
    adminManager: AdminManagerService
  ) {
    this.expressionContext = { pilexosUser: adminManager.getPilexosUser() };
  }

  ngOnInit(): void {
    this.refreshRecords();
  }

  selectConfigurationType(type: ReportConfigurationType): void {
    if (type === this.configurationType) {
      return;
    }
    this.configurationType = type;
    this.selectedRecord = null;
    this.dashboardGroupRecords = [];
    this.selectedDashboardGroupRecord = null;
    this.selectedDashboardGroupDefinition = null;
    this.previewVisible = false;
    this.editorMode = type === 'dashboard' ? 'design' : 'json';
    this.refreshRecords();
  }

  selectRecord(record: any): void {
    this.selectedRecord = record;
    const configValue = record?.[this.configFieldName];
    this.configText = typeof configValue === 'string'
      ? configValue
      : (configValue ? JSON.stringify(configValue, null, 2) : '');
    this.originalRecordSnapshot = this.clone(record);
    this.parseWorkingDefinition();
    if (this.configurationType === 'dashboard') {
      this.loadReusableWidgets();
      this.selectDashboardGroupForRecord(record);
    }
    this.previewVisible = false;
    this.previewDefinition = null;
    this.statusMessage = null;
    this.errorMessage = null;
  }

  newConfiguration(): void {
    const values = this.newRecordValues();
    this.selectRecord(this.ormDataService.createDummyEntity(this.itemName, values));
  }

  selectDashboardTab(tab: ReportDashboardGroupTabDefinition): void {
    const dashboard = this.records.find(record => record.DashboardKey === tab.dashboardKey);
    if (!dashboard) {
      this.errorMessage = `Dashboard ${tab.dashboardKey} could not be found.`;
      return;
    }
    this.selectRecord(dashboard);
  }

  openAddDashboardTab(): void {
    if (!this.selectedDashboardGroupRecord || !this.selectedDashboardGroupDefinition) {
      this.errorMessage = 'Select or create a dashboard group before adding a dashboard tab.';
      return;
    }

    const modalConfig = new DwModalConfig();
    modalConfig.title = 'Add Dashboard Tab';
    modalConfig.component = ReportDashboardCreateModalComponent;
    modalConfig.buttons = DwModalButtonStandardConfigs.instance.SaveAndCancel;
    modalConfig.modalSize = DwModalSize.large;
    modalConfig.cloneData = true;
    modalConfig.data = {
      groupDisplay: this.selectedDashboardGroupRecord.DashboardGroupDisplay,
      existingDashboardKeys: this.records.map(record => record.DashboardKey),
      defaultDataSourceItemName: this.workingDefinition?.dataSourceItemName || 'roCheckrideFactBase'
    };

    this.modalService.showModal(modalConfig).subscribe(result => {
      if (!result.cancelled && result.data) {
        this.createDashboardTab(result.data as ReportDashboardCreateModalResult);
      }
    });
  }

  openDashboardGroupSettings(): void {
    if (!this.selectedDashboardGroupRecord || !this.selectedDashboardGroupDefinition) {
      this.errorMessage = 'Select a dashboard group before configuring its tabs.';
      return;
    }

    const modalConfig = new DwModalConfig();
    modalConfig.title = 'Dashboard Group Settings';
    modalConfig.component = ReportDashboardGroupModalComponent;
    modalConfig.buttons = DwModalButtonStandardConfigs.instance.SaveAndCancel;
    modalConfig.modalSize = DwModalSize.extraLarge;
    modalConfig.cloneData = true;
    modalConfig.data = {
      groupDisplay: this.selectedDashboardGroupRecord.DashboardGroupDisplay,
      definition: this.selectedDashboardGroupDefinition,
      dashboardRecords: this.records as ReportDashboardConfigRecord[],
      widgetRecords: this.reusableWidgetRecords
    };

    this.modalService.showModal(modalConfig).subscribe(result => {
      if (!result.cancelled && result.data) {
        const modalResult = result.data as ReportDashboardGroupModalResult;
        this.saveDashboardGroup(modalResult.definition, modalResult.dashboardClones);
      }
    });
  }

  isSelectedDashboardTab(tab: ReportDashboardGroupTabDefinition): boolean {
    return this.configurationType === 'dashboard'
      && this.selectedRecord?.DashboardKey === tab.dashboardKey;
  }

  saveConfiguration(): void {
    if (!this.selectedRecord?.[this.keyFieldName] || !this.selectedRecord?.[this.displayFieldName]) {
      this.errorMessage = 'Key and display are required.';
      return;
    }

    if (!this.synchronizeEditor()) {
      return;
    }
    this.selectedRecord[this.configFieldName] = this.configText;
    this.validateSelected().subscribe(() => this.persistSelected(), error => {
      this.errorMessage = error?.message || error || 'Configuration JSON is invalid.';
    });
  }

  loadPreview(): void {
    if (this.configurationType !== 'dashboard' || !this.selectedRecord) {
      return;
    }
    if (!this.synchronizeEditor()) {
      return;
    }
    this.selectedRecord.DashboardConfigJSON = this.configText;
    this.loading = true;
    this.configService.parseDashboardRecord(this.selectedRecord as ReportDashboardConfigRecord).subscribe(definition => {
      this.previewDefinition = definition;
      this.previewVisible = true;
      this.loading = false;
      this.errorMessage = null;
    }, error => {
      this.loading = false;
      this.errorMessage = error?.message || error || 'Dashboard preview could not be loaded.';
    });
  }

  recordKey(record: any): string {
    return record?.[this.keyFieldName];
  }

  recordDisplay(record: any): string {
    return record?.[this.displayFieldName];
  }

  selectEditorMode(mode: 'design' | 'json'): void {
    if (mode === this.editorMode) {
      return;
    }

    if (mode === 'design' && !this.parseWorkingDefinition()) {
      return;
    }
    if (mode === 'json') {
      this.syncJsonFromDefinition();
    }
    this.editorMode = mode;
    this.errorMessage = null;
  }

  onDefinitionChanged(definition: ReportDashboardDefinition): void {
    this.workingDefinition = definition;
    this.syncJsonFromDefinition();
    this.statusMessage = null;
    this.errorMessage = null;
    this.previewVisible = false;
  }

  cancelChanges(): void {
    if (!this.originalRecordSnapshot) {
      return;
    }
    this.selectedRecord = this.clone(this.originalRecordSnapshot);
    const configValue = this.selectedRecord[this.configFieldName];
    this.configText = typeof configValue === 'string'
      ? configValue
      : JSON.stringify(configValue, null, 2);
    this.parseWorkingDefinition();
    this.previewVisible = false;
    this.previewDefinition = null;
    this.errorMessage = null;
    this.statusMessage = 'Changes discarded.';
  }

  validateConfiguration(): void {
    if (!this.synchronizeEditor()) {
      return;
    }
    this.selectedRecord[this.configFieldName] = this.configText;
    this.loading = true;
    this.validateSelected().subscribe(() => {
      this.loading = false;
      this.errorMessage = null;
      this.statusMessage = 'Configuration is valid.';
    }, error => {
      this.loading = false;
      this.statusMessage = null;
      this.errorMessage = error?.message || error || 'Configuration is invalid.';
    });
  }

  get itemName(): string {
    switch (this.configurationType) {
      case 'group': return 'ReportDashboardGroup';
      case 'widget': return 'ReportDashboardWidget';
      default: return 'ReportDashboard';
    }
  }

  get idFieldName(): string {
    return `${this.itemName}Id`;
  }

  get keyFieldName(): string {
    switch (this.configurationType) {
      case 'group': return 'DashboardGroupKey';
      case 'widget': return 'WidgetKey';
      default: return 'DashboardKey';
    }
  }

  get displayFieldName(): string {
    switch (this.configurationType) {
      case 'group': return 'DashboardGroupDisplay';
      case 'widget': return 'WidgetDisplay';
      default: return 'DashboardDisplay';
    }
  }

  get configFieldName(): string {
    switch (this.configurationType) {
      case 'group': return 'DashboardGroupConfigJSON';
      case 'widget': return 'WidgetConfigJSON';
      default: return 'DashboardConfigJSON';
    }
  }

  private refreshRecords(selectId?: number): void {
    this.loading = true;
    const groups = this.configurationType === 'dashboard'
      ? this.configService.loadDashboardGroupRecords()
      : of([] as ReportDashboardGroupConfigRecord[]);
    forkJoin([this.loadRecords(), groups]).subscribe(([records, groupRecords]) => {
      this.records = records || [];
      this.dashboardGroupRecords = groupRecords || [];
      this.loading = false;
      const selected = selectId
        ? this.records.find(record => record[this.idFieldName] === selectId)
        : this.records[0];
      if (selected) {
        this.selectRecord(selected);
      }
    }, error => {
      this.loading = false;
      this.errorMessage = error?.message || error || 'Dashboard configurations could not be loaded.';
    });
  }

  private loadRecords(): Observable<any[]> {
    switch (this.configurationType) {
      case 'group': return this.configService.loadDashboardGroupRecords();
      case 'widget': return this.configService.loadReusableWidgetRecords();
      default: return this.configService.loadDashboardRecords();
    }
  }

  private validateSelected(): Observable<any> {
    try {
      switch (this.configurationType) {
        case 'group':
          return of(this.configService.parseDashboardGroupRecord(this.selectedRecord));
        case 'widget':
          return of(this.configService.parseReusableWidgetRecord(this.selectedRecord));
        default:
          return this.configService.parseDashboardRecord(this.selectedRecord);
      }
    } catch (error) {
      return throwError(error);
    }
  }

  private persistSelected(): void {
    this.saving = true;
    this.errorMessage = null;
    this.statusMessage = null;
    this.ormDataService.saveEntity(this.selectedRecord).subscribe(saved => {
      this.selectedRecord = saved;
      const savedConfig = saved[this.configFieldName];
      this.configText = typeof savedConfig === 'string'
        ? savedConfig
        : JSON.stringify(savedConfig, null, 2);
      this.originalRecordSnapshot = this.clone(saved);
      this.parseWorkingDefinition();
      this.saving = false;
      this.statusMessage = 'Configuration saved.';
      this.refreshRecords(saved[this.idFieldName]);
    }, error => {
      this.saving = false;
      this.errorMessage = error?.message || error || 'Configuration could not be saved.';
    });
  }

  private newRecordValues(): any {
    switch (this.configurationType) {
      case 'group':
        return {
          DashboardGroupKey: '',
          DashboardGroupDisplay: 'New Dashboard Group',
          DashboardGroupConfigJSON: '{\n  "schemaVersion": 1,\n  "title": "New Dashboard Group",\n  "contextNames": [],\n  "tabs": []\n}',
          IsActive: true
        };
      case 'widget':
        return {
          WidgetKey: '',
          WidgetDisplay: 'New Reusable Widget',
          WidgetConfigJSON: '{\n  "schemaVersion": 1,\n  "title": "New Widget",\n  "type": "custom"\n}',
          IsActive: true
        };
      default:
        return {
          DashboardKey: '',
          DashboardDisplay: 'New Dashboard',
          DashboardConfigJSON: '{\n  "schemaVersion": 1,\n  "title": "New Dashboard",\n  "widgets": []\n}',
          IsActive: true
        };
    }
  }

  private synchronizeEditor(): boolean {
    if (this.configurationType === 'dashboard' && this.editorMode === 'design') {
      this.syncJsonFromDefinition();
      return true;
    }
    const parsed = this.parseWorkingDefinition();
    if (parsed && this.configurationType === 'dashboard') {
      this.syncJsonFromDefinition();
    }
    return parsed;
  }

  private parseWorkingDefinition(): boolean {
    try {
      if (!this.configText) {
        this.workingDefinition = null;
        return false;
      }
      this.workingDefinition = JSON.parse(this.configText);
      if (this.configurationType === 'dashboard') {
        delete this.workingDefinition.contextNames;
        this.workingDefinition = this.removeFilterVisibility(this.workingDefinition);
        this.workingDefinition.filters = this.workingDefinition.filters || [];
        this.workingDefinition.summaries = this.workingDefinition.summaries || [];
        this.workingDefinition.tiles = this.workingDefinition.tiles || [];
        this.workingDefinition.widgets = this.workingDefinition.widgets || [];
        this.workingDefinition.drillDowns = this.workingDefinition.drillDowns || [];
      }
      this.errorMessage = null;
      return true;
    } catch (error) {
      this.workingDefinition = null;
      this.errorMessage = `Configuration JSON is invalid: ${error?.message || error}`;
      return false;
    }
  }

  private syncJsonFromDefinition(): void {
    if (this.workingDefinition) {
      this.configText = JSON.stringify(this.workingDefinition, null, 2);
    }
  }

  private loadReusableWidgets(): void {
    this.configService.loadReusableWidgetRecords().subscribe(records => {
      this.reusableWidgetRecords = (records || []).filter(record => record.IsActive);
    }, () => {
      this.reusableWidgetRecords = [];
    });
  }

  private selectDashboardGroupForRecord(record: ReportDashboardConfigRecord): void {
    if (!record || !this.dashboardGroupRecords.length) {
      this.selectedDashboardGroupRecord = null;
      this.selectedDashboardGroupDefinition = null;
      return;
    }

    if (this.selectedDashboardGroupDefinition?.tabs
      ?.some(tab => tab.dashboardKey === record.DashboardKey)) {
      return;
    }

    const resolvedGroups = this.dashboardGroupRecords.map(groupRecord => {
      try {
        return {
          record: groupRecord,
          definition: this.configService.parseDashboardGroupRecord(groupRecord)
        };
      } catch (_error) {
        return null;
      }
    }).filter(group => !!group);

    const selected = resolvedGroups.find(group =>
      group.definition.tabs.some(tab => tab.dashboardKey === record.DashboardKey))
      || resolvedGroups.find(group => group.record.IsActive)
      || resolvedGroups[0];

    this.selectedDashboardGroupRecord = selected?.record || null;
    this.selectedDashboardGroupDefinition = selected?.definition || null;
    this.resolveDesignerContexts();
  }

  private createDashboardTab(values: ReportDashboardCreateModalResult): void {
    const dashboardRecord = this.ormDataService.createDummyEntity('ReportDashboard', {
      DashboardKey: values.dashboardKey,
      DashboardDisplay: values.dashboardDisplay,
      DashboardConfigJSON: JSON.stringify({
        schemaVersion: 1,
        title: values.title,
        description: values.description || undefined,
        iconClass: values.iconClass || undefined,
        dataSourceItemName: values.dataSourceItemName || undefined,
        allowPrint: true,
        layoutColumnCount: 12,
        filters: [],
        summaries: [],
        tiles: [],
        widgets: [],
        drillDowns: []
      }, null, 2),
      IsActive: values.isActive
    });
    const groupRecord = this.clone(this.selectedDashboardGroupRecord);
    const groupDefinition = this.clone(this.selectedDashboardGroupDefinition);
    groupDefinition.tabs.push({
      dashboardKey: values.dashboardKey,
      display: values.tabDisplay,
      iconClass: values.iconClass || undefined
    });
    if (!groupDefinition.defaultDashboardKey) {
      groupDefinition.defaultDashboardKey = values.dashboardKey;
    }
    const storedGroupDefinition: any = { ...groupDefinition };
    delete storedGroupDefinition.key;
    groupRecord.DashboardGroupConfigJSON = JSON.stringify(storedGroupDefinition, null, 2);

    this.creatingDashboard = true;
    this.errorMessage = null;
    this.statusMessage = null;
    this.ormDataService.saveEntity(dashboardRecord).pipe(
      mergeMap(savedDashboard => this.ormDataService.saveEntity(groupRecord).pipe(
        map(savedGroup => ({ savedDashboard, savedGroup }))
      ))
    ).subscribe(({ savedDashboard, savedGroup }) => {
      this.creatingDashboard = false;
      const groupIndex = this.dashboardGroupRecords.findIndex(group =>
        group.ReportDashboardGroupId === savedGroup.ReportDashboardGroupId);
      if (groupIndex >= 0) {
        this.dashboardGroupRecords.splice(groupIndex, 1, savedGroup);
      }
      this.selectedDashboardGroupRecord = savedGroup;
      this.selectedDashboardGroupDefinition = this.configService.parseDashboardGroupRecord(savedGroup);
      this.resolveDesignerContexts();
      this.records = [...this.records, savedDashboard];
      this.selectRecord(savedDashboard);
      this.statusMessage = `Dashboard ${values.dashboardDisplay} was created and added to ${savedGroup.DashboardGroupDisplay}.`;
    }, error => {
      this.creatingDashboard = false;
      this.errorMessage = error?.message || error || 'The dashboard tab could not be created.';
    });
  }

  private saveDashboardGroup(
    definition: ReportDashboardGroupDefinition,
    dashboardClones: ReportDashboardPendingClone[] = []
  ): void {
    const groupRecord = this.clone(this.selectedDashboardGroupRecord);
    const storedDefinition: any = this.clone(definition);
    delete storedDefinition.key;
    groupRecord.DashboardGroupConfigJSON = JSON.stringify(storedDefinition, null, 2);

    try {
      this.configService.parseDashboardGroupRecord(groupRecord);
    } catch (error) {
      this.errorMessage = error?.message || error || 'Dashboard group configuration is invalid.';
      return;
    }

    this.savingGroup = true;
    this.errorMessage = null;
    this.statusMessage = null;
    const createClones$ = dashboardClones.length
      ? forkJoin(dashboardClones.map(clone => this.configService.createDashboardRecord(clone.record)))
      : of([] as ReportDashboardConfigRecord[]);
    createClones$.pipe(
      mergeMap(savedClones => this.ormDataService.saveEntity(groupRecord).pipe(
        map(savedGroup => ({ savedClones, savedGroup }))
      ))
    ).subscribe(({ savedClones, savedGroup }) => {
      this.savingGroup = false;
      const groupIndex = this.dashboardGroupRecords.findIndex(group =>
        group.ReportDashboardGroupId === savedGroup.ReportDashboardGroupId);
      if (groupIndex >= 0) {
        this.dashboardGroupRecords.splice(groupIndex, 1, savedGroup);
      }
      this.selectedDashboardGroupRecord = savedGroup;
      this.selectedDashboardGroupDefinition = this.configService.parseDashboardGroupRecord(savedGroup);
      this.resolveDesignerContexts();
      if (savedClones.length) {
        this.records = [...this.records, ...savedClones];
        this.selectRecord(savedClones[savedClones.length - 1]);
      }
      this.statusMessage = dashboardClones.length
        ? `${dashboardClones.length} dashboard${dashboardClones.length === 1 ? '' : 's'} cloned and added to ${savedGroup.DashboardGroupDisplay}.`
        : `Dashboard group ${savedGroup.DashboardGroupDisplay} was updated.`;
    }, error => {
      this.savingGroup = false;
      this.errorMessage = error?.message || error || 'Dashboard group could not be saved.';
    });
  }

  private clone<T>(value: T): T {
    return value ? JSON.parse(JSON.stringify(value)) : value;
  }

  private resolveDesignerContexts(): void {
    const contextNames = this.selectedDashboardGroupDefinition?.contextNames || [];
    this.contextService.waitForContextDefLoad().pipe(
      take(1),
      mergeMap(() => this.contextService.getContextsAndResolve(contextNames, {})),
      take(1)
    ).subscribe(context => this.expressionContext = context || {});
  }

  private removeFilterVisibility(definition: any): any {
    if (!definition || typeof definition !== 'object' || !Array.isArray(definition.filters)) {
      return definition;
    }
    return {
      ...definition,
      filters: definition.filters.map(filter => {
        const normalized = { ...(filter || {}) };
        delete normalized.visibleExpression;
        return normalized;
      })
    };
  }
}
